/* global CustomFunctions */
import { getDerivativePricing } from "../../libs/cognito-api/derivative-pricing/derivative-pricing";
import type { MTMCalculationResultDto } from "../../libs/cognito-api/schemas/mtmcalculation-result-dto";
import { getPreviousWorkingDay, normalizeDate } from "../../shared/calendar";

const {
  postDerivativePricingPricing,
  postDerivativePricingMtm,
  postDerivativePricingImpliedVol,
} = getDerivativePricing();

type DateInput = string | number;

export function normalizePricingDate(value: DateInput): string {
  return normalizeDate(value);
}

export function normalizeOptionalPricingDate(value?: DateInput | null): string | null {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  return normalizeDate(value);
}

async function resolveDataBase(dataBase?: DateInput | null): Promise<string> {
  return normalizeOptionalPricingDate(dataBase) ?? await getPreviousWorkingDay();
}

export async function priceFx(
  productType: string,
  currency: string,
  maturityDate: DateInput,
  strike?: number,
  strike2?: number,
  dataBase?: DateInput,
  spotOverride?: number,
  carryOverride?: number,
  preOverride?: number,
  volOverride?: number
): Promise<number> {
  const effectiveDataBase = await resolveDataBase(dataBase);
  const response = await postDerivativePricingPricing({
    productType,
    maturityDate: normalizePricingDate(maturityDate),
    strike: strike ?? null,
    strike2: strike2 ?? null,
    currency,
    dataBase: effectiveDataBase,
    spotOverride: spotOverride ?? null,
    carryRateOverride: carryOverride ?? null,
    preRateOverride: preOverride ?? null,
    volatilityOverride: volOverride ?? null,
  });

  if (!response.status || response.data == null) {
    throw new CustomFunctions.Error(
      CustomFunctions.ErrorCode.invalidValue,
      response.message || "Erro na API Cognito"
    );
  }

  const value = extractPricingValue(productType, response.data);
  if (value == null) {
    throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, "Resultado sem valor de precificacao");
  }
  return value;
}

export async function mtmFx(
  productType: string,
  currency: string,
  maturityDate: DateInput,
  tradeRate: number,
  notional?: number,
  strike?: number,
  strike2?: number,
  tradeDate?: DateInput,
  dataBase?: DateInput,
  spotOverride?: number,
  carryOverride?: number,
  preOverride?: number,
  volOverride?: number
): Promise<number> {
  const effectiveDataBase = await resolveDataBase(dataBase);
  const response = await postDerivativePricingMtm({
    productType,
    maturityDate: normalizePricingDate(maturityDate),
    tradeRate,
    notional: notional ?? null,
    strike: strike ?? null,
    strike2: strike2 ?? null,
    currency,
    tradeDate: normalizeOptionalPricingDate(tradeDate),
    dataBase: effectiveDataBase,
    spotOverride: spotOverride ?? null,
    carryRateOverride: carryOverride ?? null,
    preRateOverride: preOverride ?? null,
    volatilityOverride: volOverride ?? null,
  });

  if (!response.status || response.data == null) {
    throw new CustomFunctions.Error(
      CustomFunctions.ErrorCode.invalidValue,
      response.message || "Erro na API Cognito"
    );
  }

  const value = response.data.netEstrutura != null ? response.data.netEstrutura : response.data.mtmNdf;
  if (value == null) {
    throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, "Resultado sem valor de MtM");
  }
  return value;
}

export async function impliedVolFx(
  productType: string,
  currency: string,
  maturityDate: DateInput,
  strike: number,
  strike2?: number,
  notional?: number,
  unitPremium?: number,
  totalPremium?: number,
  tradeRate?: number,
  dataBase?: DateInput
): Promise<number> {
  const effectiveDataBase = await resolveDataBase(dataBase);
  const response = await postDerivativePricingImpliedVol({
    productType,
    maturityDate: normalizePricingDate(maturityDate),
    strike,
    strike2: strike2 ?? null,
    notional: notional ?? null,
    unitPremium: unitPremium ?? null,
    totalPremium: totalPremium ?? null,
    tradeRate: tradeRate ?? null,
    currency,
    dataBase: effectiveDataBase,
  });

  if (!response.status || response.data == null) {
    throw new CustomFunctions.Error(
      CustomFunctions.ErrorCode.invalidValue,
      response.message || "Erro na API Cognito"
    );
  }

  if (response.data.impliedVolPercent != null) return response.data.impliedVolPercent;
  if (response.data.impliedVolDecimal != null) return response.data.impliedVolDecimal * 100;
  throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, "Resultado sem valor de volatilidade");
}

function extractPricingValue(productType: string, d: MTMCalculationResultDto): number | null {
  switch (productType.toLowerCase()) {
    case "ndfvanilla":  return d.forwardNdf ?? null;
    case "ndfcap":      return d.netEstrutura ?? d.ndfMaisCallVendida ?? null;
    case "callspread":  return d.custoCallSpread ?? null;
    case "callvanilla": return d.precoCallComprada ?? null;
    case "putvanilla":  return d.precoPutVanilla ?? null;
    case "putspread":   return d.custoPutSpread ?? null;
    case "straddle":    return d.custoStraddle ?? null;
    case "strangle":    return d.custoStrangle ?? null;
    case "collar":      return d.custoCollar ?? null;
    case "fence":       return d.fenceTotal ?? d.custoOpcoesFence ?? null;
    default:             return d.forwardNdf ?? d.netEstrutura ?? d.taxaAtual ?? null;
  }
}
